<template>
  <div>
    <!-- État de chargement -->
    <b-card
      v-if="isLoading"
      class="p-3"
      aria-busy="true"
      aria-live="polite"
    >
      <div class="d-flex justify-content-center">
        <b-spinner
          variant="primary"
          label="Chargement..."
          aria-label="Chargement des informations du manager"
        />
      </div>
    </b-card>

    <!-- État d'erreur -->
    <b-alert
      v-else-if="error"
      variant="danger"
      show
      aria-live="assertive"
      aria-label="Erreur lors du chargement des informations du manager"
    >
      {{ error }}
    </b-alert>

    <!-- Formulaire de modification -->
    <b-card
      v-else
      title="Modifier le manager"
      aria-label="Formulaire de modification du manager"
    >
      <validation-observer
        ref="formValidator"
        v-slot="{ handleSubmit }"
      >
        <b-form
          aria-label="Formulaire de modification du manager"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- En-tête manager -->
          <div
            class="manager-header mb-4"
            aria-label="Résumé du manager"
          >
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(form.username)"
                variant="light-primary"
                size="lg"
                :aria-label="`Avatar du manager ${form.username}`"
              />
              <div class="ml-2">
                <h4
                  class="mb-0"
                  aria-label="Nom du manager"
                >
                  {{ form.username }}
                </h4>
                <small
                  class="text-muted"
                  aria-label="Adresse e-mail professionnelle"
                >{{ form.email }}</small>
              </div>
            </div>
          </div>

          <!-- Section informations personnelles -->
          <div
            class="form-section mb-4"
            aria-label="Section informations personnelles du manager"
          >
            <h5 class="section-title">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
                size="18"
                aria-hidden="true"
              />
              Informations personnelles du manager
            </h5>

            <b-row>
              <!-- Username field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Nom complet"
                  rules="required"
                  vid="username"
                  mode="eager"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="Nom complet *"
                    label-for="username"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="username"
                        v-model="form.username"
                        :state="errors.length > 0 ? false : (valid && form.username ? true : null)"
                        placeholder="Entrez le nom complet"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && form.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Email field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Email"
                  :rules="`required|email|asyncEmail:${managerId}`"
                  vid="email"
                  mode="eager"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="Email *"
                    label-for="email"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="email"
                        v-model="form.email"
                        type="email"
                        :state="errors.length > 0 ? false : (valid && form.email ? true : null)"
                        placeholder="Entrez l'adresse email"
                        class="premium-input"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          v-b-tooltip.hover="emailGenerationTooltip"
                          icon="RefreshCwIcon"
                          :class="form.username && form.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          @click="form.username && form.username.trim().length > 0 ? generateCompanyEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Phone field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Téléphone"
                  :rules="`required|tnPhone|asyncPhone:,${managerId}`"
                  vid="phone"
                  mode="eager"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="téléphone professionnel *"
                    label-for="phone"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="form.phone"
                        placeholder="Entrez le numéro de téléphone"
                        :formatter="formatPhone"
                        :state="errors.length > 0 ? false : (valid && form.phone ? true : null)"
                        maxlength="8"
                        class="premium-input"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && form.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                    <b-form-text v-else>
                      Format: 8 chiffres, sans espaces
                    </b-form-text>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Phone2 field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="téléphone personnel"
                  rules="numeric|min:8"
                  vid="phone2"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="téléphone personnel"
                    label-for="phone2"
                  >
                    <b-form-input
                      id="phone2"
                      v-model="form.phone2"
                      class="premium-input"
                      placeholder="Entrez le numéro de téléphone personnel (optionnel)"
                      :formatter="formatPhone"
                      :state="errors.length > 0 ? false : null"
                      maxlength="8"
                      @keypress="allowOnlyNumbers"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- CIN field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="CIN"
                  rules="numeric|min:8"
                  vid="cin"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="CIN"
                    label-for="cin"
                  >
                    <b-form-input
                      id="cin"
                      v-model="form.cin"
                      class="premium-input"
                      placeholder="Entrez le numéro CIN"
                      :formatter="formatCIN"
                      :state="errors.length > 0 ? false : null"
                      maxlength="8"
                      @keypress="allowOnlyNumbers"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <b-form-text>Format: 8 chiffres, sans espaces</b-form-text>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Address field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors }"
                  name="Adresse"
                  vid="address"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="Adresse"
                    label-for="address"
                  >
                    <b-form-textarea
                      id="address"
                      v-model="form.address"
                      placeholder="Entrez l'adresse complète"
                      rows="3"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </div>

          <!-- Assignment Section -->
          <div class="form-section mb-4">
            <h5 class="section-title">
              <feather-icon
                icon="MapPinIcon"
                class="mr-1"
                size="18"
              />
              Affectation et statut
            </h5>

            <b-row>
              <!-- Repository field -->
              <b-col md="6">
                <validation-provider
                  #default="{ errors, valid }"
                  name="Dépôt"
                  rules="required"
                  vid="repository"
                  mode="eager"
                >
                  <b-form-group
                    label-class="form-label-premium"
                    label="Dépôt *"
                    label-for="repository"
                  >
                    <div class="position-relative">
                      <v-select
                        id="repository"
                        v-model="form.repository"
                        :options="repositories"
                        label="name"
                        :reduce="repo => repo.id"
                        placeholder="Sélectionnez un dépôt"
                        :class="[
                          'enhanced-select premium-select',
                          errors.length > 0 ? 'is-invalid' : (valid && form.repository ? 'is-valid' : '')
                        ]"
                      />
                      <feather-icon
                        v-if="valid && form.repository"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon select-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Active status toggle -->
              <b-col
                md="6"
                class="d-flex align-items-center"
              >
                <b-form-group
                  label-class="form-label-premium"
                  class="mb-0 w-100"
                >
                  <label class="d-block mb-2">Statut du compte</label>
                  <b-form-checkbox
                    v-model="form.is_active"
                    switch
                    size="lg"
                    class="custom-control-success"
                  >
                    <span :class="form.is_active ? 'text-success' : 'text-danger'">
                      {{ form.is_active ? 'Actif' : 'Inactif' }}
                    </span>
                  </b-form-checkbox>
                  <small class="text-muted d-block mt-1">
                    {{ form.is_active ? "Le manager peut se connecter à son compte" : "Le manager ne peut pas se connecter à son compte" }}
                  </small>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Submit buttons -->
          <div class="d-flex justify-content-between mt-3">
            <b-button
              variant="outline-secondary"
              :to="{ name: 'manager-list' }"
              class="btn-min-width"
            >
              <feather-icon
                icon="XIcon"
                size="16"
                class="mr-1"
              />
              Annuler
            </b-button>

            <span
              id="manager-edit-save-btn-wrapper"
              class="d-inline-block"
            >
              <b-button
                type="submit"
                variant="primary"
                :disabled="!isFormValid || isSubmitting"
                :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                class="btn-min-width"
              >
                <b-spinner
                  v-if="isSubmitting"
                  small
                  class="mr-1"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  size="16"
                  class="mr-1"
                />
                {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications' }}
              </b-button>
            </span>
            <b-tooltip
              v-if="!isFormValid || isSubmitting"
              target="manager-edit-save-btn-wrapper"
              triggers="hover"
              placement="top"
            >
              {{ tooltipMessage }}
            </b-tooltip>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import {
  required, email, numeric, min, length,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BAvatar,
  BRow,
  BCol,
  BSpinner,
  BFormText,
  BAlert,
  BFormCheckbox,
  BTooltip,
} from 'bootstrap-vue'

// Register the validation rules
extend('required', required)
extend('email', email)
extend('numeric', numeric)
extend('min', min)
extend('length', length)

export default {
  components: {
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BAvatar,
    BRow,
    BCol,
    BSpinner,
    BFormText,
    BAlert,
    BFormCheckbox,
    BTooltip,
  },

  data() {
    return {
      isLoading: false, // Force form to display for debugging
      isSubmitting: false,
      error: null,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        repository: null,
        is_active: true,
        cin: '',
      },
      repositories: [],
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.form.username && this.form.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du manager'
    },

    isFormValid() {
      if (!this.form) return false
      return (
        this.form.username !== ''
        && this.form.email !== ''
        && this.form.phone !== ''
        && this.form.repository !== null
      )
    },

    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'
      if (!this.form) return 'Chargement des données...'

      const missing = []
      if (!this.form.username || !this.form.username.trim()) missing.push('Nom')
      if (!this.form.email || !this.form.email.trim()) missing.push('Email')
      if (!this.form.phone || !this.form.phone.trim()) missing.push('Téléphone')
      if (!this.form.repository) missing.push('Dépôt')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  created() {
    (async () => {
      try {
        await Promise.all([
          this.fetchManagerDetails(),
          this.fetchRepositories(),
        ])
      } catch (err) {
        this.error = 'Erreur lors du chargement des données'
      } finally {
        this.isLoading = false
      }
    })()
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    generateCompanyEmail() {
      if (!this.form.username || !this.form.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du manager.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.form.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.form.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    async fetchManagerDetails() {
      try {
        const { id } = this.$route.params

        // First, ensure repositories are loaded
        await this.fetchRepositories()

        // Then fetch manager details
        const { data } = await axios.get(`/api/auth/users/managers/${id}/`)

        // Now populate form with retrieved data after repositories are loaded
        this.form = {
          username: data.username || '',
          email: data.email || '',
          phone: data.phone || '',
          phone2: data.phone2 || '',
          address: data.address || '',
          repository: data.repository?.id || data.repository || null,
          is_active: data.is_active !== undefined ? data.is_active : true,
          cin: data.cin || '',
        }
      } catch (error) {
        this.error = 'Erreur lors du chargement des informations du manager'
        throw error
      }
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.error = 'Erreur lors du chargement des dépôts'
        throw error
      }
    },

    formatCIN(value) {
      // Allow only digits and limit to 8 characters
      return value?.replace(/\D/g, '').substring(0, 8) || ''
    },

    formatPhone(value) {
      return value?.replace(/\D/g, '').substring(0, 8) || ''
    },

    allowOnlyNumbers(event) {
      const { charCode } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    async onSubmit() {
      this.isSubmitting = true

      try {
        const { id } = this.$route.params

        // Create payload based on form data
        const payload = {
          username: this.form.username,
          email: this.form.email,
          phone: this.form.phone,
          phone2: this.form.phone2,
          address: this.form.address,
          repository: this.form.repository,
          is_active: this.form.is_active,
          cin: this.form.cin,
        }

        await axios.put(`/api/auth/users/update/${id}/`, payload)

        this.showToast('success', 'Manager modifié avec succès')
        this.$router.push({ name: 'manager-list' })
      } catch (error) {
        // Handle errors
        // eslint-disable-next-line no-console
        console.error('Error updating manager:', error)

        let errorMessage = 'Erreur lors de la mise à jour du manager'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (typeof errorData === 'object' && errorData !== null) {
            const messages = []
            // Handle both { field: [error] } and { errors: { field: [error] } }
            const fieldsToProcess = (errorData.errors && typeof errorData.errors === 'object')
              ? errorData.errors
              : errorData

            Object.keys(fieldsToProcess).forEach(field => {
              const fieldError = fieldsToProcess[field]
              const rawError = Array.isArray(fieldError) ? fieldError[0] : fieldError
              if (typeof rawError === 'string') {
                if (rawError.includes('existe') || rawError.includes('already exists')) {
                  if (field === 'phone') messages.push('Ce numéro de téléphone est déjà utilisé.')
                  else if (field === 'email') messages.push('Cette adresse email est déjà utilisée.')
                  else if (field === 'username') messages.push('Ce nom est déjà utilisé.')
                  else messages.push(`Le champ ${field} est déjà utilisé.`)
                } else {
                  messages.push(rawError)
                }
              }
            })

            if (messages.length > 0) {
              errorMessage = messages.join(' ')
            } else if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.detail) {
              errorMessage = errorData.detail
            }
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        }

        this.showToast('danger', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.manager-header {
  padding: 1.5rem;
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background-color: #f3f2f7;
  }
}

.form-section {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border-left: 3px solid #9b1c22;

  .section-title {
    display: flex;
    align-items: center;
    color: #5e5873;
    font-weight: 500;
    margin-bottom: 1.25rem;

    i, svg {
      margin-right: 0.5rem;
    }
  }
}

.enhanced-select {
  .vs__dropdown-toggle {
    padding: 0.571rem 0.5rem;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:hover {
      border-color: #9b1c22;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #9b1c22;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, 0.1);
  }

  .vs__selected {
    margin: 0 0.25rem;
    color: #5e5873;
  }

  .vs__dropdown-menu {
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

    .vs__dropdown-option--highlight {
      background: #9b1c22;
      color: #fff;
    }
  }
  cursor: pointer !important;
}

.btn-min-width {
  min-width: 150px;
}

.custom-control-success .custom-control-input:checked ~ .custom-control-label::before {
  border-color: #28c76f;
  background-color: #28c76f;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #9b1c22;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
